import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiChevronDown, FiExternalLink, FiDollarSign, FiStar, FiUsers, FiCalendar,
  FiCheck, FiFlag, FiX, FiMessageSquare, FiSend
} from 'react-icons/fi';
import { api } from '../../api';

// Source → spine node color
const SOURCE_COLORS = {
  public: '#2a5298',
  internal: '#7c3aed',
};

// Event type → node color override (for non-intelligence types)
const TYPE_COLORS = {
  position: '#16a34a',
  milestone: '#ea580c',
  meeting: '#0891b2',
};

const STATUS_CONFIG = {
  raw: { label: 'Raw', icon: null, className: 'status-raw' },
  confirmed: { label: 'Confirmed', icon: FiCheck, className: 'status-confirmed' },
  flagged: { label: 'Flagged', icon: FiFlag, className: 'status-flagged' },
  dismissed: { label: 'Dismissed', icon: FiX, className: 'status-dismissed' },
};

function TimelineEventCard({ event, showCompany = true, onEventUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [noteInput, setNoteInput] = useState('');
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [localEvent, setLocalEvent] = useState(event);

  const nodeColor = TYPE_COLORS[localEvent.event_type] || SOURCE_COLORS[localEvent.source] || '#6b7280';

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (saving || newStatus === localEvent.status) return;
    setSaving(true);
    try {
      await api.updateTimelineEvent(localEvent.id, { status: newStatus });
      setLocalEvent({ ...localEvent, status: newStatus });
      onEventUpdated?.({ ...localEvent, status: newStatus });
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleNoteSubmit = async () => {
    if (saving || !noteInput.trim()) return;
    setSaving(true);
    try {
      await api.updateTimelineEvent(localEvent.id, { gp_note: noteInput.trim() });
      setLocalEvent({ ...localEvent, gp_note: noteInput.trim() });
      onEventUpdated?.({ ...localEvent, gp_note: noteInput.trim() });
      setShowNoteInput(false);
    } catch (err) {
      console.error('Failed to save note:', err);
    } finally {
      setSaving(false);
    }
  };

  const significanceClass = localEvent.significance === 'high' ? 'significance-high' : '';
  const statusClass = localEvent.status === 'unscraped' ? 'status-unscraped' : '';
  const currentStatus = localEvent.status || 'raw';
  const statusConf = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.raw;

  return (
    <div className="timeline-event" style={{ '--node-color': nodeColor }}>
      <div className="timeline-node" />
      <div
        className={`timeline-event-card ${significanceClass} ${statusClass}`}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Top row: date + category + status badge + sentiment + expand */}
        <div className="timeline-event-top">
          <div className="timeline-event-meta">
            {localEvent.event_date && (
              <span className="timeline-event-date">
                {formatDate(localEvent.event_date)}
              </span>
            )}
            {!localEvent.event_date && (
              <span className="timeline-event-date" style={{ fontStyle: 'italic' }}>
                Date unknown
              </span>
            )}
            {showCompany && localEvent.company_name && (
              <Link
                to={`/timeline/${encodeURIComponent(localEvent.company_name)}`}
                className="timeline-event-company"
                onClick={(e) => e.stopPropagation()}
              >
                {localEvent.company_name}
              </Link>
            )}
            <span className={`timeline-category-badge badge-${localEvent.category}`}>
              {localEvent.category}
            </span>
            {/* GP status badge — only for editable events with non-raw status */}
            {localEvent.editable && currentStatus !== 'raw' && (
              <span className={`timeline-gp-status-badge ${statusConf.className}`}>
                {statusConf.icon && <statusConf.icon size={10} />}
                {statusConf.label}
              </span>
            )}
          </div>
          <div className="timeline-event-actions">
            {localEvent.sentiment && (
              <span
                className={`timeline-sentiment-dot sentiment-${localEvent.sentiment}`}
                title={`Sentiment: ${localEvent.sentiment}`}
              />
            )}
            <button
              className={`timeline-expand-btn ${expanded ? 'expanded' : ''}`}
              onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
              title={expanded ? 'Collapse' : 'Expand'}
            >
              <FiChevronDown size={16} />
            </button>
          </div>
        </div>

        {/* Headline */}
        <p className={`timeline-event-headline ${localEvent.significance === 'high' ? 'bold' : ''}`}>
          {localEvent.headline}
        </p>

        {/* GP Note display (always visible if exists) */}
        {localEvent.gp_note && (
          <div className="timeline-gp-note" onClick={(e) => e.stopPropagation()}>
            <FiMessageSquare size={12} />
            <span>{localEvent.gp_note}</span>
          </div>
        )}

        {/* Expandable detail */}
        {expanded && (
          <div className="timeline-event-detail" onClick={(e) => e.stopPropagation()}>
            {localEvent.detail && <p>{localEvent.detail}</p>}
            {localEvent.key_data && (
              <p style={{ fontWeight: 500 }}>{localEvent.key_data}</p>
            )}

            {/* GP Actions — only for editable (GSheet-backed) events */}
            {localEvent.editable && (
              <div className="timeline-gp-actions">
                <span className="timeline-gp-actions-label">Review:</span>
                <div className="timeline-status-buttons">
                  {Object.entries(STATUS_CONFIG).map(([key, conf]) => (
                    <button
                      key={key}
                      className={`timeline-status-btn ${conf.className} ${currentStatus === key ? 'active' : ''}`}
                      onClick={() => handleStatusChange(key)}
                      disabled={saving}
                      title={conf.label}
                    >
                      {conf.icon && <conf.icon size={12} />}
                      {conf.label}
                    </button>
                  ))}
                </div>
                <button
                  className={`timeline-note-toggle ${showNoteInput ? 'active' : ''}`}
                  onClick={() => {
                    setShowNoteInput(!showNoteInput);
                    if (!showNoteInput) setNoteInput(localEvent.gp_note || '');
                  }}
                  title="Add note"
                >
                  <FiMessageSquare size={14} />
                </button>
              </div>
            )}

            {/* Note input */}
            {showNoteInput && localEvent.editable && (
              <div className="timeline-note-input-row">
                <input
                  type="text"
                  className="timeline-note-input"
                  placeholder="Add a note..."
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleNoteSubmit()}
                  autoFocus
                />
                <button
                  className="timeline-note-submit"
                  onClick={handleNoteSubmit}
                  disabled={saving || !noteInput.trim()}
                >
                  <FiSend size={14} />
                </button>
              </div>
            )}

            {/* Footer meta */}
            <div className="timeline-event-footer">
              {localEvent.fund && (
                <span className="timeline-event-footer-item">
                  <FiDollarSign size={12} />
                  {localEvent.fund}
                </span>
              )}
              {localEvent.source_name && (
                <span className="timeline-event-footer-item">
                  <FiStar size={12} />
                  {localEvent.source_name}
                  {localEvent.source_tier && ` (Tier ${localEvent.source_tier})`}
                </span>
              )}
              {localEvent.attendees && localEvent.attendees.length > 0 && (
                <span className="timeline-event-footer-item">
                  <FiUsers size={12} />
                  {localEvent.attendees.join(', ')}
                </span>
              )}
              {localEvent.amount_usd && (
                <span className="timeline-event-footer-item">
                  <FiDollarSign size={12} />
                  ${localEvent.amount_usd.toLocaleString()}
                </span>
              )}
              {localEvent.source_url && (
                <a
                  href={localEvent.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="timeline-event-link"
                >
                  <FiExternalLink size={12} />
                  Source
                </a>
              )}
              <span className="timeline-event-footer-item">
                <FiCalendar size={12} />
                {localEvent.source}{localEvent.event_type && localEvent.event_type !== 'intelligence' ? ` · ${localEvent.event_type}` : ''}
              </span>
              {localEvent.affinity_url && (
                <a
                  href={localEvent.affinity_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="timeline-event-link timeline-affinity-link"
                >
                  Affinity
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TimelineEventCard;
